import './Notification.css';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getAnnouncements, deleteAnnouncement, waitForNewAnnouncements, getNotificationMessages, deleteNotificationMessage, deleteReportTicket, connectChatSocket } from '../../api/communication';
import { getCurrentUser, getUser, updateUser } from '../../api/users_institutions';
import { playNotificationSound } from '../../utils/notificationSound';
import Report from './Report';

// If a long-poll request itself errors (network blip, server restart, etc.) before the server
// can even hold it open, retry after a short pause instead of hammering the endpoint in a tight
// loop.
const RETRY_AFTER_ERROR_MS = 3000;

const Notification = () => {
    const { t, i18n } = useTranslation();

    const [announcements, setAnnouncements] = useState([]);
    const [announcementToDelete, setAnnouncementToDelete] = useState(null);

    const [inboxNotifications, setInboxNotifications] = useState([]);
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    // Notification ids whose underlying report was just "unsubmitted" (ReportTicket deleted) --
    // tracked locally rather than refetching, since the notification itself isn't re-sent by the
    // backend after that delete (only notif.report gets nulled out server-side via SET_NULL).
    const [unsubmittedReportNotifIds, setUnsubmittedReportNotifIds] = useState(new Set());
    // The ReportTicket id currently open in the "Review Report" modal (see Report.jsx), or null
    // when it's closed.
    const [reviewingReportId, setReviewingReportId] = useState(null);

    const fetchInboxNotifications = () => {
        getNotificationMessages().then(data => {
            setInboxNotifications(Array.isArray(data) ? data : []);
        }).catch(error => {
            console.error('Error fetching notification messages:', error);
        });
    };

    const formatDate = (value) => {
        if (!value) return '';
        // e.g. "Aug 5, 2026, 09:26 PM" -- locale-aware (respects the app's current language)
        // rather than the browser-default numeric "8/5/2026, 9:26:37 PM" toLocaleString gives.
        return new Intl.DateTimeFormat(i18n.language, {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
        }).format(new Date(value));
    };

    // Bell-icon badge (#notifBadge appears several times -- mobile/tablet/desktop variants
    // sharing the same id -- so this targets the shared class instead). Scoped to its own
    // ".bell-notif-badge" class (distinct from Chat.jsx's ".chat-notif-badge") so clearing it can
    // never also hide the unrelated chat-unread badge. Driven by the server-side
    // User.new_announcement/new_notification flags (see api/models.py) rather than a client-only
    // "last seen id" heuristic, so it stays correct across devices/sessions for the same account
    // -- the bell lights up if either is set, since it opens both tabs at once. Both flags are
    // cleared server-side together when the user opens the notification box (Navbar.jsx's
    // toggleNotifications), not from here.
    const setBellBadgeVisible = (visible) => {
        document.querySelectorAll('.bell-notif-badge').forEach(el => {
            el.style.display = visible ? 'flex' : 'none';
        });
    };

    // The two .tab-badge spans inside the dropdown (#announcementBadge/#inboxBadge) -- distinct
    // from the bell badge above, these let the user tell which specific tab has something new
    // once the dropdown is already open, since the bell itself is one combined signal for both.
    // Driven by the same server-side flags as the bell; switchNotifTab below only ever clears
    // whichever one the user actually clicks into, so this is the only place that ever shows
    // them again afterward (a new announcement/notification arriving re-sets its own flag
    // server-side regardless of whether the bell was already opened and cleared earlier).
    const setTabBadgesVisible = (announcementVisible, notificationVisible) => {
        const announcementBadge = document.getElementById('announcementBadge');
        const inboxBadge = document.getElementById('inboxBadge');
        if (announcementBadge) announcementBadge.style.display = announcementVisible ? 'flex' : 'none';
        if (inboxBadge) inboxBadge.style.display = notificationVisible ? 'flex' : 'none';
    };

    const refreshNotificationFlag = () => {
        const currentUser = getCurrentUser();
        if (!currentUser?.id) return;
        getUser(currentUser.id).then(user => {
            setBellBadgeVisible(!!(user.new_announcement || user.new_notification));
            setTabBadgesVisible(!!user.new_announcement, !!user.new_notification);
        }).catch(error => {
            console.error('Error checking notification flag:', error);
        });
    };

    // Initial fetch, then a long-poll loop that only resolves once a new announcement actually
    // exists (or after the server's own ~25s timeout) -- each response is immediately followed
    // by the next request, so a new announcement shows up here (and lights the bell badge) as
    // soon as it's created instead of waiting on a fixed polling interval.
    //
    // Each waiting client ties up one server thread for up to ~25s at a time (see
    // announcements_wait in api/views/communication.py), and Notification mounts globally in
    // Navbar -- every logged-in user on every page holds one. Backgrounded tabs are the common
    // case (this sits behind a bell icon, not a screen anyone stares at), so the loop is paused
    // via the Page Visibility API whenever the tab isn't visible and resumed when it comes back,
    // instead of re-issuing continuously regardless of whether anyone could see the result. A
    // request already in flight when the tab backgrounds just runs out its current ~25s wait
    // (not worth wiring up an AbortController for) -- it's not re-issued after that.
    useEffect(() => {
        let cancelled = false;
        let paused = document.visibilityState === 'hidden';
        let afterId = 0;

        const waitLoop = () => {
            if (cancelled || paused) return;
            waitForNewAnnouncements(afterId).then(newOnes => {
                if (cancelled) return;
                if (newOnes.length > 0) {
                    afterId = Math.max(afterId, ...newOnes.map(a => a.id));
                    setAnnouncements(prev => [...newOnes, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date)));
                    refreshNotificationFlag();
                    playNotificationSound();
                }
                waitLoop();
            }).catch(error => {
                console.error('Error long-polling for announcements:', error);
                if (!cancelled) setTimeout(waitLoop, RETRY_AFTER_ERROR_MS);
            });
        };

        const handleVisibilityChange = () => {
            const wasPaused = paused;
            paused = document.visibilityState === 'hidden';
            // Only kick the loop back off here if it was actually parked -- otherwise this would
            // fire a second, redundant request alongside whatever's already in flight.
            if (wasPaused && !paused) waitLoop();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        getAnnouncements().then(data => {
            if (cancelled) return;
            setAnnouncements(data);
            afterId = data.length ? Math.max(...data.map(a => a.id)) : 0;
            waitLoop();
        }).catch(error => {
            console.error('Error fetching announcements:', error);
            waitLoop();
        });
        fetchInboxNotifications();
        refreshNotificationFlag();

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // Deliberately empty -- waitLoop/refreshNotificationFlag are recreated every render, and
        // including them would tear down and restart the long-poll loop on every render instead
        // of running it once for the component's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // NotificationMessages have no long-poll endpoint of their own (unlike announcements) --
    // the backend already pushes a 'notification_message' event over the chat websocket the
    // moment one is created (see notification_messages() in views/communication.py), targeted at
    // exactly the receiver, so listening for it here is what makes the bell badge (and the inbox
    // list, if it's already been opened once this session) react without needing a page reload.
    // This opens its own connection alongside Chat.jsx's (each websocket connection a user has
    // open gets the push independently, per notify_user's docstring) rather than plumbing a
    // shared socket through Navbar -- a second idle connection is a small enough cost for not
    // restructuring how Chat/Notification are mounted as siblings.
    useEffect(() => {
        const disconnect = connectChatSocket((event) => {
            if (event?.event === 'notification_message') {
                refreshNotificationFlag();
                fetchInboxNotifications();
                playNotificationSound();
            }
        });
        return disconnect;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on outside click -- a single document-level listener attached once (not on every
    // render, which the previous version did by calling addEventListener directly in the
    // component body with no cleanup, leaking one more listener per render).
    useEffect(() => {
        const handleClickOutside = (e) => {
            const notifBox = document.getElementById('notifBox');
            const notifBtn = document.querySelector('.notification-btn');
            if (!notifBox || !notifBtn) return;
            if (notifBox.classList.contains('show') && !notifBox.contains(e.target) && !notifBtn.contains(e.target)) {
                notifBox.classList.remove('show');
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const switchNotifTab = (tab) => {
        const tabs = document.querySelectorAll('.notif-tab');
        const contents = document.querySelectorAll('.notif-content');

        tabs.forEach(t => t.classList.remove('active'));
        contents.forEach(c => c.classList.remove('active'));

        // Clears this tab's own flag server-side the moment it's actually viewed (rather than
        // relying solely on Navbar.jsx's toggleNotifications clearing both together on bell-open),
        // so a later refreshNotificationFlag() call -- e.g. a new announcement arriving while
        // this tab sits unviewed -- can't accidentally re-derive "unread" for a tab the user has
        // already looked at.
        const currentUser = getCurrentUser();

        if (tab === 'announcements') {
        tabs[0].classList.add('active');
        document.getElementById('notif-announcements').classList.add('active');
        document.getElementById('announcementBadge').style.display = 'none';
        if (currentUser?.id) {
            updateUser(currentUser.id, { new_announcement: false }).catch(error => {
                console.error('Error clearing announcement flag:', error);
            });
        }
        } else {
        tabs[1].classList.add('active');
        document.getElementById('notif-inbox').classList.add('active');
        document.getElementById('inboxBadge').style.display = 'none';
        if (currentUser?.id) {
            updateUser(currentUser.id, { new_notification: false }).catch(error => {
                console.error('Error clearing notification flag:', error);
            });
        }
        fetchInboxNotifications();
        }
    }

    const senderName = (ann) => {
        const name = [ann.sender_first_name, ann.sender_last_name].filter(Boolean).join(' ');
        return name || ann.sender_username || t('notification.unknownSender');
    };

    const handleDeleteAnnouncement = async () => {
        const id = announcementToDelete;
        if (id == null) return;
        try {
            await deleteAnnouncement(id);
            setAnnouncements(prev => prev.filter(ann => ann.id !== id));
        } catch (error) {
            console.error('Error deleting announcement:', error);
        } finally {
            setAnnouncementToDelete(null);
        }
    };

    // The receiver's own delete -- backend allows this for the notification's own receiver (or a
    // superuser), same permission shape as handleDeleteAnnouncement's superuser-only delete.
    const handleDeleteNotification = async () => {
        const id = notificationToDelete;
        if (id == null) return;
        try {
            await deleteNotificationMessage(id);
            setInboxNotifications(prev => prev.filter(notif => notif.id !== id));
        } catch (error) {
            console.error('Error deleting notification message:', error);
        } finally {
            setNotificationToDelete(null);
        }
    };

    // Lets the author of a just-submitted report withdraw it, from the report_submission
    // notification itself -- this replaces the cancel action "My Reports" used to offer, now
    // that it's gone. Only actionable while the ticket is still 'open' (see notif.report_status
    // and report_ticket_detail's DELETE in views/communication.py, which rejects this once staff
    // has moved it to 'in_progress' or resolved it).
    const handleUnsubmitReport = async (notif) => {
        if (!notif.report) return;
        try {
            await deleteReportTicket(notif.report);
            setUnsubmittedReportNotifIds((prev) => new Set(prev).add(notif.id));
        } catch (error) {
            console.error('Error unsubmitting report:', error);
        }
    };

    return (
        <>
        <div id="notifBox" className="notif-box">
        <div className="notif-tabs">
            <button className="notif-tab active" onClick={() => switchNotifTab('announcements')}>
            {t('notification.announcementsTab')} <span className="tab-badge" id="announcementBadge" style={{ display: 'none' }}>!</span>
            </button>
            <button className="notif-tab" onClick={() => switchNotifTab('inbox')}>
            {t('notification.inboxTab')} <span className="tab-badge" id="inboxBadge" style={{ display: 'none' }}>!</span>
            </button>
        </div>
        <div id="notif-announcements" className="notif-content active">
            { announcements.length > 0 ? (
                announcements.map((ann) => (
                    <div key={ann.id} className={`notif-item ${ann.is_important ? 'important' : ''}`}>
                        <div className="notif-subject">
                            <p style={ ann.is_important && ann.subject.length > 16 ? {maxWidth: '100px' } : {}}>{ann.subject}</p>
                            {ann.is_important && <span className="important-badge">{t('notification.veryImportant')}</span>}
                            {ann.sender_username === getCurrentUser().username && <button className="delete-btn" style={{    
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--del-color)',
                                marginLeft: 'auto',
                                marginRight: '0px',
                                minWidth: '35px',
                                minHeight: '32px',
                                border: 'none',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'center'
                            }} onClick={() => setAnnouncementToDelete(ann.id)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'flex', flexShrink: 0, marginTop: '6px' }}>
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                            </button>}
                        </div>
                        <div className="notif-text" dangerouslySetInnerHTML={{ __html: ann.message }}></div>
                        <div className="notif-footer">
                            <div className="notif-sender">{senderName(ann)}</div>
                            <span className="notif-date">{formatDate(ann.date)}</span>
                        </div>
                    </div>
                ))) : (
                    <p className="empty-notif">{t('notification.emptyInbox')}</p>
                )
            }
        </div>
        <div id="notif-inbox" className="notif-content">
            { inboxNotifications.length > 0 ? (
                inboxNotifications.map((notif) => (
                    <div key={notif.id} className={`notif-item ${notif.is_important ? 'important' : ''}`}>
                        <div className="notif-subject">
                            <p style={ notif.is_important && notif.subject.length > 16 ? {maxWidth: '100px' } : {}}>{notif.subject}</p>
                            {notif.is_important && <span className="important-badge">{t('notification.veryImportant')}</span>}
                            <button className="delete-btn" style={{
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--del-color)',
                                marginLeft: 'auto',
                                marginRight: '0px',
                                minWidth: '35px',
                                minHeight: '32px',
                                border: 'none',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'center'
                            }} onClick={() => setNotificationToDelete(notif.id)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'flex', flexShrink: 0, marginTop: '6px' }}>
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                            </button>
                        </div>
                        <div className="notif-text" dangerouslySetInnerHTML={{ __html: notif.message }}></div>
                        <div className="notif-footer">
                            {notif.type === 'report_submission' && notif.report && (
                                <>
                                    <button type="button" className="notif-review-btn" onClick={() => setReviewingReportId(notif.report)}>
                                        {t('notification.reviewReport')}
                                    </button>
                                    {unsubmittedReportNotifIds.has(notif.id) ? (
                                        <div className="notif-report-canceled">{t('notification.reportCanceled')}</div>
                                    ) : notif.report_status === 'open' ? (
                                        <button type="button" className="notif-unsubmit-btn" onClick={() => handleUnsubmitReport(notif)}>
                                            {t('notification.unsubmitReport')}
                                        </button>
                                    ) : (
                                        <div className="notif-report-locked">{t('notification.cannotUnsubmit')}</div>
                                    )}
                                </>
                            )}
                            <span className="notif-date">{formatDate(notif.date)}</span>
                        </div>
                    </div>
                ))) : (
                    <p className="empty-notif">{t('notification.emptyInbox')}</p>
                )
            }
        </div>
        </div>
        {announcementToDelete != null && (
            <div className="notif-confirm-overlay" onClick={() => setAnnouncementToDelete(null)}>
                <div className="notif-confirm-box" onClick={(e) => e.stopPropagation()}>
                    <h3 className="notif-confirm-title">{t('notification.confirmDeleteTitle')}</h3>
                    <p className="notif-confirm-body">{t('notification.confirmDeleteBody')}</p>
                    <div className="notif-confirm-actions">
                        <button className="notif-confirm-cancel" onClick={() => setAnnouncementToDelete(null)}>{t('common.cancel')}</button>
                        <button className="notif-confirm-delete" onClick={handleDeleteAnnouncement}>{t('notification.confirmDeleteButton')}</button>
                    </div>
                </div>
            </div>
        )}
        {notificationToDelete != null && (
            <div className="notif-confirm-overlay" onClick={() => setNotificationToDelete(null)}>
                <div className="notif-confirm-box" onClick={(e) => e.stopPropagation()}>
                    <h3 className="notif-confirm-title">{t('notification.confirmDeleteNotifTitle')}</h3>
                    <p className="notif-confirm-body">{t('notification.confirmDeleteNotifBody')}</p>
                    <div className="notif-confirm-actions">
                        <button className="notif-confirm-cancel" onClick={() => setNotificationToDelete(null)}>{t('common.cancel')}</button>
                        <button className="notif-confirm-delete" onClick={handleDeleteNotification}>{t('notification.confirmDeleteButton')}</button>
                    </div>
                </div>
            </div>
        )}
        {reviewingReportId != null && (
            <Report reportId={reviewingReportId} onClose={() => setReviewingReportId(null)} />
        )}
        </>
    );
};

export default Notification;
